import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from './Toast'
import { canModerate } from '../lib/supabase'
import { REACTIONS } from '../lib/reactions'
import RankBadge from './RankBadge'

const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSei7zcQ7kRy7yK-FrBC5EqgaFD1zuYiQJWLsxdwIJFNTnki7w/viewform'



export default function PostModal({ postId, onClose, onNavigateWriter }) {
  const { user, profile } = useAuth()
  const toast = useToast()
  const [post, setPost] = useState(null)
  const [comments, setComments] = useState([])
  const [relatedPosts, setRelatedPosts] = useState([])
  const [commentText, setCommentText] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [readProgress, setReadProgress] = useState(0)
  const [myReactions, setMyReactions] = useState({})
  const [reactionCounts, setReactionCounts] = useState({})
  const [readingMode, setReadingMode] = useState('default') // 'default' | 'dark'
  const [saved, setSaved] = useState(false)
  const [copyDone, setCopyDone] = useState(false)
  const overlayRef = useRef(null)
  const scrollRef = useRef(null)
  const contentRef = useRef(null)

  useEffect(() => {
    fetchPost()
    fetchComments()
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [postId])

  useEffect(() => {
    if (user) { fetchMyReactions(); fetchReactionCounts() }
    else fetchReactionCounts()
  }, [postId, user])

  useEffect(() => {
    // Check saved state
    if (user) {
      const saved = JSON.parse(localStorage.getItem(`margins_saved_${user.id}`) || '[]')
      setSaved(saved.includes(postId))
    }
  }, [postId, user])

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Reading progress bar
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el
      const progress = scrollHeight <= clientHeight ? 100 : (scrollTop / (scrollHeight - clientHeight)) * 100
      setReadProgress(Math.min(100, progress))
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [loading])

  async function fetchPost() {
    const { data, error } = await supabase
      .from('posts')
      .select('*, profiles!posts_author_id_fkey(id, display_name, role, column_inches, is_voice)')
      .eq('id', postId)
      .single()
    if (error || !data) {
      // Try without the extended profile fields in case migration hasn't run
      const { data: fallback, error: err2 } = await supabase
        .from('posts')
        .select('*, profiles!posts_author_id_fkey(id, display_name, role)')
        .eq('id', postId)
        .single()
      if (err2 || !fallback) { onClose(); return }
      setPost(fallback)
      setLoading(false)
      return
    }
    setPost(data)
    setLoading(false)
    // Fetch related
    if (data.section || data.tag) {
      const { data: related } = await supabase
        .from('posts')
        .select('id, title, tag, section, excerpt, profiles!posts_author_id_fkey(display_name, column_inches, is_voice)')
        .eq('status', 'published')
        .eq('section', data.section)
        .neq('id', postId)
        .limit(3)
      setRelatedPosts(related || [])
    }
  }

  async function fetchComments() {
    const { data } = await supabase
      .from('comments')
      .select('*, profiles!comments_author_id_fkey(id, display_name, role, column_inches, is_voice)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true })
    setComments(data || [])
  }

  async function fetchMyReactions() {
    const { data } = await supabase
      .from('reactions')
      .select('reaction_type')
      .eq('post_id', postId)
      .eq('user_id', user.id)
    const map = {}
    data?.forEach(r => { map[r.reaction_type] = true })
    setMyReactions(map)
  }

  async function fetchReactionCounts() {
    const { data } = await supabase
      .from('reactions')
      .select('reaction_type')
      .eq('post_id', postId)
    const counts = {}
    data?.forEach(r => { counts[r.reaction_type] = (counts[r.reaction_type] || 0) + 1 })
    setReactionCounts(counts)
  }

  async function toggleReaction(type) {
    if (!user) { toast('Sign in to react to pieces.', 'error'); return }
    if (post?.author_id === user?.id) { toast('You cannot react to your own posts.', 'error'); return }
    if (myReactions[type]) {
      await supabase.from('reactions').delete()
        .eq('post_id', postId).eq('user_id', user.id).eq('reaction_type', type)
      setMyReactions(prev => { const n = { ...prev }; delete n[type]; return n })
      setReactionCounts(prev => ({ ...prev, [type]: Math.max(0, (prev[type] || 1) - 1) }))
    } else {
      await supabase.from('reactions').upsert({ post_id: postId, user_id: user.id, reaction_type: type })
      setMyReactions(prev => ({ ...prev, [type]: true }))
      setReactionCounts(prev => ({ ...prev, [type]: (prev[type] || 0) + 1 }))
    }
  }

  function toggleSave() {
    if (!user) { toast('Sign in to save pieces.', 'error'); return }
    const key = `margins_saved_${user.id}`
    const list = JSON.parse(localStorage.getItem(key) || '[]')
    const next = saved
      ? list.filter(id => id !== postId)
      : [...list, postId]
    localStorage.setItem(key, JSON.stringify(next))
    setSaved(!saved)
    toast(saved ? 'Removed from reading list.' : 'Saved to reading list.', 'success')
  }

  function sharePost() {
    const url = `${window.location.origin}/post/${postId}`
    navigator.clipboard.writeText(url).then(() => {
      setCopyDone(true)
      setTimeout(() => setCopyDone(false), 2000)
    })
  }

  async function submitComment(e) {
    e.preventDefault()
    if (!commentText.trim()) return
    setSubmitting(true)
    const { error } = await supabase
      .from('comments')
      .insert({ post_id: postId, author_id: user.id, content: commentText.trim() })
    if (error) toast(error.message, 'error')
    else { setCommentText(''); fetchComments(); toast('Comment posted.', 'success') }
    setSubmitting(false)
  }

  async function deleteComment(commentId) {
    if (!window.confirm('Delete this comment?')) return
    const { error } = await supabase.from('comments').delete().eq('id', commentId)
    if (error) toast(error.message, 'error')
    else { fetchComments(); toast('Comment deleted.', 'success') }
  }

  async function deletePost() {
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    await supabase.from('posts').delete().eq('id', postId)
    toast('Post deleted.', 'success')
    onClose()
  }

  // Reject post with optional note
  async function rejectPost() {
    const note = window.prompt('Optional: leave a note for the author explaining why this was rejected (they will see this in their dashboard):')
    if (note === null) return // cancelled
    await supabase.from('posts').update({
      status: 'rejected',
      rejection_note: note.trim() || null
    }).eq('id', postId)
    toast('Post rejected.', 'success')
    onClose()
  }

  async function publishPost() {
    await supabase.from('posts').update({ status: 'published' }).eq('id', postId)
    toast('Post published.', 'success')
    setPost(prev => ({ ...prev, status: 'published' }))
  }

  const isReadingDark = readingMode === 'dark'
  const bgColor = isReadingDark ? '#0a0908' : 'var(--paper)'
  const textColor = isReadingDark ? '#ddd5c8' : 'var(--ink)'

  const date = post ? new Date(post.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  }) : ''

  return (
    <div ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()} className="post-modal-overlay">
      {/* Reading progress bar */}
      <div style={{ ...styles.progressBar, width: `${readProgress}%` }} />

      <div className="post-modal" data-reading-dark={isReadingDark ? "true" : undefined} style={{ background: bgColor }}>
        {/* Header bar */}
        <div className="post-modal-header" style={{ background: isReadingDark ? '#111' : 'var(--cream)', borderColor: isReadingDark ? '#222' : 'var(--rule)' }}>
          <span style={styles.backLabel}>
            {post ? ('the feed') : ''}
          </span>
          <div className="post-modal-header-actions">
            {/* Reading mode toggle */}
            <button onClick={() => setReadingMode(m => m === 'dark' ? 'default' : 'dark')} style={styles.iconBtn} title="Toggle reading mode">
              {isReadingDark ? '☀' : '☾'}
            </button>
            {/* Save */}
            <button onClick={toggleSave} style={{ ...styles.iconBtn, color: saved ? 'var(--accent)' : undefined }} title={saved ? 'Remove from reading list' : 'Save to reading list'}>
              {saved ? '◆' : '◇'}
            </button>
            {/* Share */}
            <button onClick={sharePost} style={styles.iconBtn} title="Copy link">
              {copyDone ? '✓' : '⤴'}
            </button>
            {/* Mod actions */}
            {canModerate(profile?.role) && post && post.status === 'pending' && (
              <>
                <button className="btn btn-sm" style={{ background: 'var(--success)', color: '#fff', border: 'none' }} onClick={publishPost}>Publish</button>
                <button className="btn btn-sm btn-danger" onClick={rejectPost}>Reject</button>
              </>
            )}
            {canModerate(profile?.role) && post && (
              <button className="btn btn-sm btn-danger" onClick={deletePost}>Delete</button>
            )}
            {post && post.status !== 'published' && (
              <span className={`status-badge status-${post.status}`}>{post.status}</span>
            )}
            <button onClick={onClose} style={styles.closeBtn} title="Close (Esc)">✕</button>
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} className="post-modal-body">
          {loading ? (
            <div style={styles.loadingWrap}>
              <div style={styles.loadingText}>The Margins<span style={{ color: 'var(--accent)' }}>.</span></div>
            </div>
          ) : !post ? null : (
            <div ref={contentRef} className="post-modal-content" style={{ color: textColor }} data-reading-dark={isReadingDark ? "true" : undefined}>
              <div>
                <div className="post-modal-tag">{post.tag || post.section}</div>
                <h1 className="post-modal-title" style={{ color: textColor }}>{post.title}</h1>
                <div className="post-modal-meta">
                  <button
                    onClick={() => onNavigateWriter && onNavigateWriter(post.profiles?.id)}
                    style={{ ...styles.metaAuthorBtn, color: 'var(--accent)' }}
                  >
                    {post.profiles?.display_name || 'The Margins'}
                  </button>
                  <span style={{ display: 'flex', gap: '1rem', color: 'var(--muted)' }}>
                    {post.read_time && <span>{post.read_time} min read</span>}
                    <span>{date}</span>
                  </span>
                </div>

                {/* Cover image */}
                {post.cover_image && (
                  <div style={styles.coverImageWrap}>
                    <img src={post.cover_image} alt={post.title} style={styles.coverImage} />
                  </div>
                )}
                {post.excerpt && <p style={{ ...styles.excerpt, color: isReadingDark ? '#a09880' : 'var(--muted)' }}>{post.excerpt}</p>}

                {/* Body — supports both HTML (rich editor) and plain text */}
                <div style={styles.body} className="post-body">
                  {post.body.includes('<') ? (
                    <div dangerouslySetInnerHTML={{ __html: post.body }} style={{ color: textColor }} />
                  ) : (
                    post.body.split('\n\n').map((para, i) => (
                      <p key={i} style={{ ...styles.para, color: textColor }}>{para}</p>
                    ))
                  )}
                </div>

                {/* Second image */}
                {post.image_2 && (
                  <div style={{ ...styles.coverImageWrap, marginTop: '2rem' }}>
                    <img src={post.image_2} alt="" style={styles.coverImage} />
                  </div>
                )}

                {/* Reactions */}
                <div className="post-modal-reactions">
                  <p className="post-modal-reactions-label">How did this land?</p>
                  <div className="post-modal-reactions-list">
                    {REACTIONS.map(r => (
                      <button
                        key={r.key}
                        onClick={() => toggleReaction(r.key)}
                        style={{
                          ...styles.reactionBtn,
                          ...(myReactions[r.key] ? styles.reactionActive : {}),
                          ...(post?.author_id === user?.id ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                        }}
                        title={post?.author_id === user?.id ? 'You cannot react to your own posts' : r.desc}
                      >
                        <span style={styles.reactionSymbol}>{r.symbol}</span>
                        <span style={styles.reactionInner}>
                          <span style={styles.reactionLabel}>{r.label}</span>
                          <span style={styles.reactionDesc}>{r.desc}</span>
                        </span>
                        {reactionCounts[r.key] > 0 && (
                          <span style={styles.reactionCount}>{reactionCounts[r.key]}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Related posts */}
                {relatedPosts.length > 0 && (
                  <div className="post-modal-related">
                    <h3 style={styles.relatedTitle}>Also from {'the feed'}</h3>
                    <div className="post-modal-related-grid">
                      {relatedPosts.map(r => (
                        <button key={r.id} style={styles.relatedCard} onClick={() => {
                          // Navigate to this post — handled by parent swapping postId
                          if (onClose) { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('openPost', { detail: r.id })), 50) }
                        }}>
                          <div style={styles.relatedTag}>{r.tag || r.section}</div>
                          <div style={styles.relatedCardTitle}>{r.title}</div>
                          <div style={styles.relatedAuthor}>{r.profiles?.display_name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                <div style={styles.commentsSection}>
                  <div style={styles.commentsHeader}>
                    <h2 style={{ ...styles.commentsTitle, color: textColor }}>Discussion</h2>
                    <span style={styles.commentCount}>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
                  </div>

                  {user ? (
                    <form onSubmit={submitComment} style={styles.commentForm}>
                      <textarea
                        className="form-textarea"
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        placeholder="Share your thoughts..."
                        style={{ minHeight: 90 }}
                      />
                      <button type="submit" className="btn btn-ink btn-sm" disabled={submitting || !commentText.trim()}>
                        {submitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </form>
                  ) : (
                    <div style={styles.signInPrompt}>
                      <p style={styles.signInText}>
                        <a href="/auth" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>Sign in</a> to join the discussion.
                      </p>
                    </div>
                  )}

                  <div style={styles.commentsList}>
                    {comments.length === 0 ? (
                      <div className="empty-state" style={{ border: 'none', padding: '2rem 0' }}>
                        <p>No comments yet. Start the discussion.</p>
                      </div>
                    ) : comments.map(comment => (
                      <div key={comment.id} style={styles.comment}>
                        <div style={styles.commentHeader}>
                          <div style={styles.commentAuthor}>
                            <button
                              style={styles.commentAvatarBtn}
                              onClick={() => onNavigateWriter && onNavigateWriter(comment.profiles?.id)}
                            >
                              <span style={styles.commentAvatar}>{comment.profiles?.display_name?.[0]?.toUpperCase()}</span>
                            </button>
                            <span style={styles.commentName}>{comment.profiles?.display_name}</span>
                            <span className={`tag tag-role-${comment.profiles?.role}`}>{comment.profiles?.role}</span>
                            <RankBadge columnInches={comment.profiles?.column_inches || 0} isVoice={comment.profiles?.is_voice || false} size="sm" />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={styles.commentDate}>{new Date(comment.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {(canModerate(profile?.role) || comment.author_id === user?.id) && (
                              <button className="btn btn-sm btn-danger" onClick={() => deleteComment(comment.id)}>Delete</button>
                            )}
                          </div>
                        </div>
                        <p style={styles.commentContent}>{comment.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(15,14,12,0.75)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem', animation: 'fadeIn 0.2s ease',
  },
  progressBar: {
    position: 'fixed', top: 0, left: 0, height: 3,
    background: 'var(--accent)', zIndex: 2001,
    transition: 'width 0.1s linear',
  },
  modal: {
    width: 'min(92vw, 800px)', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    border: '1px solid var(--rule)',
    boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
    animation: 'modalSlide 0.25s cubic-bezier(0.4,0,0.2,1)',
    transition: 'background 0.3s ease',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.85rem 1.5rem', borderBottom: '1px solid var(--rule)',
    flexShrink: 0,
  },
  backLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '0.62rem',
    letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)',
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  iconBtn: {
    background: 'none', border: '1px solid var(--rule)',
    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', color: 'var(--muted)', cursor: 'pointer', transition: 'all 0.15s',
  },
  closeBtn: {
    background: 'none', border: '1px solid var(--rule)',
    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.8rem', color: 'var(--muted)', cursor: 'pointer',
  },
  modalBody: { overflowY: 'auto', flex: 1 },
  loadingWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6rem 2rem' },
  loadingText: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.5rem', fontWeight: 700, animation: 'pulse 1.5s ease-in-out infinite' },
  content: { padding: '3rem 3.5rem 4rem' },
  tag: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '1.25rem' },
  meta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.06em', paddingBottom: '1.75rem', borderBottom: '1px solid var(--rule)' },
  metaAuthorBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.06em', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 },
  excerpt: { fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'italic', fontSize: '1.1rem', lineHeight: 1.7, margin: '1.75rem 0', paddingBottom: '1.75rem', borderBottom: '1px solid var(--rule)' },
  body: {},
  para: { fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '1.4rem' },
  // Reactions
  reactionsSection: { marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' },
  reactionsLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '1rem' },
  reactionsList: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  reactionBtn: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.65rem 1.1rem', border: '1px solid var(--rule)',
    background: 'var(--cream)', cursor: 'pointer', transition: 'all 0.2s',
    fontFamily: "'DM Mono', monospace", textAlign: 'left',
  },
  reactionActive: { borderColor: 'var(--accent)', background: 'rgba(232,101,58,0.06)', color: 'var(--accent)' },
  reactionSymbol: { fontSize: '1.1rem', flexShrink: 0, lineHeight: 1 },
  reactionInner: { display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  reactionLabel: { fontSize: '0.65rem', letterSpacing: '0.08em', fontWeight: 500 },
  reactionDesc: { fontSize: '0.55rem', color: 'var(--muted)', letterSpacing: '0.04em', lineHeight: 1.3 },
  reactionCount: { fontSize: '0.6rem', color: 'var(--muted)', marginLeft: 'auto', alignSelf: 'center' },
  // Related
  relatedSection: { marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' },
  relatedTitle: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '1rem' },
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)' },
  coverImageWrap: { marginBottom: '1.75rem', lineHeight: 0 },
  coverImage: { width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' },
  relatedCard: { background: 'var(--paper)', padding: '1.25rem', textAlign: 'left', border: 'none', cursor: 'pointer', transition: 'background 0.15s', width: '100%' },
  relatedTag: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.4rem' },
  relatedCardTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.3, color: 'var(--ink)', marginBottom: '0.4rem' },
  relatedAuthor: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)' },
  // Comments
  commentsSection: { marginTop: '3.5rem', paddingTop: '2.5rem', borderTop: '2px solid var(--ink)' },
  commentsHeader: { display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1.75rem' },
  commentsTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700 },
  commentCount: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.1em', color: 'var(--muted)' },
  commentForm: { marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  signInPrompt: { padding: '1.25rem', background: 'var(--cream)', border: '1px solid var(--rule)', marginBottom: '1.5rem' },
  signInText: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: 'var(--muted)', textAlign: 'center' },
  commentsList: { display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)' },
  comment: { background: 'var(--paper)', padding: '1.25rem 1.5rem' },
  commentHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' },
  commentAuthor: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  commentAvatarBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  commentAvatar: { width: 26, height: 26, background: 'var(--ink)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 700, borderRadius: '50%', flexShrink: 0 },
  commentName: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.04em' },
  commentDate: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)' },
  commentContent: { fontSize: '0.92rem', lineHeight: 1.7, color: 'var(--ink)' },
}
